import { UpToTop16 } from '@carbon/icons-react';
import Close16 from '@carbon/icons-react/es/close/16';
import Restart16 from '@carbon/icons-react/es/restart/16';
import Search16 from '@carbon/icons-react/es/search/16';
import Task16 from '@carbon/icons-react/es/task/16';
import { ColumnApi } from 'ag-grid-community';
import { Tooltip, Typography } from 'antd';
import { camelCase } from 'lodash';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { FixedSizeList } from 'react-window';
import { Button } from 'components/atoms/button';
import { Checkbox } from 'components/atoms/checkbox';
import { IconShell } from 'components/atoms/icon-shell/icon-shell';
import { Input } from 'components/atoms/input';
import { ListItem } from 'components/molecules/draggable-list/components';
import { DraggableList } from 'components/molecules/draggable-list/draggable-list';
import { SearchInput } from 'components/molecules/views/saved-views/components';
import { useTranslation } from 'hooks/use-translation';
import {
  FooterContent,
  NoResultMessage,
  StyledCheckbox,
  StyledMenu,
  ListContainer,
  MenuItem,
  ListContent,
  ListContentCol,
  ListHeader,
  ListItemName,
} from './components';
import { ColumnOrder } from './types';
import { useColumnVisible } from './use-column-visible';

const { Text } = Typography;

interface CustomComponents {
  allColumns?: ReactNode;
  resetToDefault?: ReactNode;
}
export interface DropdownMenuProps {
  columnOrder: ColumnOrder[];
  gridColumnApi?: ColumnApi;
  excludedColumns: string[];
  defaultColumnOrder: string[];
  defaultVisibleColumns?: string[];
  onClose?: () => void;
  components?: CustomComponents;
}

interface ListData {
  key: string;
  title: string;
  isVisible: boolean;
  hideDrag: boolean;
  listItem: JSX.Element;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  columnOrder,
  gridColumnApi,
  excludedColumns = [],
  defaultColumnOrder,
  defaultVisibleColumns,
  onClose = () => {},
  components = {},
}) => {
  const [searchText, setSearchText] = useState('');

  const {
    allColumns,
    isAllColumnsVisible,
    toggleSingleColumnVisible,
    toggleAllColumnsVisible,
    resetDefault,
  } = useColumnVisible(
    columnOrder,
    excludedColumns,
    defaultColumnOrder,
    gridColumnApi,
    defaultVisibleColumns,
  );

  const onMoveToTop = useCallback(
    (key: string) => {
      // Move the column to the top of the list, so explicitly set the index to 0
      gridColumnApi?.moveColumn(key, 0 + excludedColumns.length);
    },
    [excludedColumns.length, gridColumnApi],
  );

  const getListItemData = useCallback(
    (isSelectedColumnsSection: boolean) =>
      (allCol: ListData[], col: ColumnOrder) => {
        const colDef = gridColumnApi?.getColumn(col.colId)?.getColDef();

        if (!colDef) {
          return allCol;
        }
        const key = colDef?.field as string;
        const title = colDef?.headerName as string;
        const isVisible = !col.hide;

        allCol.push({
          key,
          title,
          isVisible,
          hideDrag: !isVisible,
          listItem: (
            <MenuItem
              onClick={() =>
                isSelectedColumnsSection
                  ? {}
                  : toggleSingleColumnVisible(key, !isVisible)
              }
              style={{
                flexDirection: isSelectedColumnsSection ? 'row-reverse' : 'row',
              }}
            >
              {isSelectedColumnsSection ? (
                <>
                  <Button
                    icon={<Close16 />}
                    type="text"
                    size="small"
                    onClick={() => toggleSingleColumnVisible(key, !isVisible)}
                    data-cy={`columns_reorder.fields.${camelCase(
                      title,
                    )}.remove`}
                  />
                  <Tooltip
                    mouseEnterDelay={0.5}
                    title={
                      <FormattedMessage id="ag_grid.edit_columns.move_to_top" />
                    }
                  >
                    <Button
                      icon={<UpToTop16 />}
                      type="text"
                      size="small"
                      className="column-move-to-top"
                      data-cy={`columns_reorder.fields.${camelCase(
                        title,
                      )}.move_to_top`}
                      onClick={() => onMoveToTop(key)}
                    />
                  </Tooltip>
                </>
              ) : (
                <Checkbox
                  name={key}
                  checked={isVisible}
                  style={{ padding: '0 var(--spacing-03)', height: 'auto' }}
                  data-cy={`columns_reorder.fields.${camelCase(
                    title,
                  )}.checkbox`}
                />
              )}
              <ListItemName>
                <Text
                  ellipsis={{ tooltip: title }}
                  style={{
                    width: key.startsWith('project-') ? 120 : 150,
                    lineHeight: 'var(--height-base)',
                  }}
                  data-cy={`columns_reorder.fields.${camelCase(title)}`}
                >
                  {title}
                </Text>
                {key.startsWith('project-') ? (
                  <Tooltip
                    title={
                      <FormattedMessage id="task_list.messages.project_field" />
                    }
                  >
                    <IconShell icon={<Task16 />} />
                  </Tooltip>
                ) : null}
              </ListItemName>
            </MenuItem>
          ),
        });

        return allCol;
      },
    [gridColumnApi, onMoveToTop, toggleSingleColumnVisible],
  );

  const allColumnsData = useMemo(
    () => allColumns?.reduce(getListItemData(false), [] as ListData[]),
    [allColumns, getListItemData],
  );

  const allColumnsWithSearch = useMemo(() => {
    return allColumnsData.filter(
      (col) =>
        !searchText ||
        col?.title?.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [allColumnsData, searchText]);

  const selectedColumnsData = useMemo(
    () =>
      allColumns
        ?.filter((col) => !col.hide)
        ?.reduce(getListItemData(true), [] as ListData[]),
    [allColumns, getListItemData],
  );

  const onSortChanged = useCallback(
    (updatedRow, updatedRowOrder) => {
      // Find the row being replaced in the selectedColumns array
      const rowBeingReplaced = selectedColumnsData[updatedRowOrder];
      // Find the index of the row being replaced in the allColumns array
      const indexOfRowBeingReplaced = allColumns.findIndex(
        (col) => col.colId === rowBeingReplaced.key,
      );

      // Move the column to the new position based on index in the allColumns array
      gridColumnApi?.moveColumn(
        updatedRow.key,
        indexOfRowBeingReplaced + excludedColumns.length,
      );
    },
    [allColumns, excludedColumns.length, gridColumnApi, selectedColumnsData],
  );

  return (
    <StyledMenu>
      <SearchInput>
        <Input
          placeholder={useTranslation('search')}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          data-cy="columns_reorder.search-input"
        />
        {searchText ? (
          <Close16
            onClick={() => setSearchText('')}
            style={{ cursor: 'pointer' }}
            data-cy="columns_reorder.clear-search"
          />
        ) : (
          <Search16 />
        )}
      </SearchInput>
      <ListContent>
        <ListContentCol>
          <ListHeader>
            <FormattedMessage id="ag_grid.displayed_columns" /> (
            {selectedColumnsData.length})
          </ListHeader>
          <ListContainer
            itemLayout="vertical"
            split={false}
            data-cy="drag_handle.displayed_columns.wrapper"
          >
            {selectedColumnsData.length > 0 ? (
              <DraggableList
                height={316}
                listData={selectedColumnsData}
                onSortChange={onSortChanged}
                dragHandlePosition="left"
              />
            ) : (
              <NoResultMessage>
                <FormattedMessage id="ag_grid.no_selected_columns" />
              </NoResultMessage>
            )}
          </ListContainer>
        </ListContentCol>
        <ListContentCol>
          {components.allColumns ?? (
            <ListHeader onClick={toggleAllColumnsVisible}>
              <StyledCheckbox
                name="columns.view_all"
                checked={isAllColumnsVisible}
                data-cy="columns_reorder.view-all.checkbox"
              />
              <FormattedMessage id="ag_grid.all_columns" />
            </ListHeader>
          )}
          <ListContainer
            itemLayout="vertical"
            split={false}
            data-cy="drag_handle.wrapper"
          >
            {allColumnsWithSearch.length > 0 ? (
              <FixedSizeList
                height={316}
                itemCount={allColumnsWithSearch.length}
                itemSize={32}
                itemData={allColumnsWithSearch}
                width="100%"
              >
                {({ index, style }) => (
                  <div key={allColumnsWithSearch[index]?.key} style={style}>
                    <ListItem>{allColumnsWithSearch[index].listItem}</ListItem>
                  </div>
                )}
              </FixedSizeList>
            ) : (
              <NoResultMessage>
                <FormattedMessage id="filter.no_results" />
              </NoResultMessage>
            )}
          </ListContainer>
        </ListContentCol>
      </ListContent>
      <FooterContent>
        <div>
          {components.resetToDefault ?? (
            <Button
              onClick={resetDefault}
              data-cy="columns_reorder.reset_to_default"
              icon={<Restart16 />}
              iconPlacement="right"
            >
              <FormattedMessage id="ag_grid.reset_columns" />
            </Button>
          )}
        </div>
        <div>
          <Button
            onClick={onClose}
            type="primary"
            data-cy="columns_reorder.done"
          >
            <FormattedMessage id="done" />
          </Button>
        </div>
      </FooterContent>
    </StyledMenu>
  );
};
